var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LoadingButton } from '@mui/lab';
import { Box, Button, FormControlLabel, Grid, Switch, Typography, styled } from '@mui/material';
import { sortBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { StatusPermission } from '../../../enum';
import { FULL_REPORT_ROUTER_URL } from '../../../router/constants';
import { useUserPermissionsAPI } from '../../app/selectors';
import { ConfirmDialog, ErrorMessageBox, FallbackComponent } from '../../common';
import { usePendingActions } from '../../hooks';
import { useReportContext } from '../../report/context';
import { useReportOutletContext } from '../../report/hooks';
import { useForbiddenRedirect, useReportAPI } from '../../report/selectors';
import { useEffectivePlanPermission, useIsPlanLocked } from '../../wizard/selectors';
import { METRIC_LIST, SECTION_DATA } from '../constants';
import { ControlPanelSetting } from '../controlPanelSetting';
import { DataPanelLayout } from '../dataPanel';
import { METRICS_ENUM, SECTION_ENUM } from '../enum';
import { METRIC_COLORS } from './constants';
import MetricSlider from './metricSlider';
import { useMetricSettingsAPI, useUpdateMetricSettingsAPI } from './selectors';
var StyledGridItem = styled(Grid)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            paddingLeft: theme.spacing(6),
            paddingRight: theme.spacing(6),
            paddingTop: theme.spacing(3),
            paddingBottom: theme.spacing(6),
            borderBottomStyle: 'solid',
            borderBottomWidth: '1px',
            borderBottomColor: theme.palette.divider
        },
        _b[theme.breakpoints.down('md')] = {
            '&:last-child': { borderBottom: 'none' },
        },
        _b[theme.breakpoints.up('md')] = {
            '&:nth-last-of-type(1)': { borderBottom: 'none' },
            '&:nth-last-of-type(2):nth-of-type(odd)': {
                borderBottom: 'none',
            },
        },
        _b);
});
var StyledActionBox = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2),
        '& > button': { minWidth: theme.typography.pxToRem(102) },
    });
});
export var ManageSettings = function () {
    var t = useTranslation(['report', 'translation']).t;
    var _a = useParams().basePlanId, basePlanId = _a === void 0 ? '' : _a;
    var navigate = useNavigate();
    var _b = useReportAPI(), reportData = _b[0], fetchReportData = _b[1];
    var _c = useState(SECTION_ENUM.PERFORMANCE), selectedSection = _c[0], setSelectedSection = _c[1];
    var actualPermission = useUserPermissionsAPI().actualPermission;
    var effectiveActualPermission = useEffectivePlanPermission(actualPermission);
    var isPlanLocked = useIsPlanLocked();
    var disabled = effectiveActualPermission !== StatusPermission.Edit || isPlanLocked;
    var context = useReportOutletContext();
    useEffect(function () {
        var _a;
        (_a = context === null || context === void 0 ? void 0 : context.setIsDisabledExport) === null || _a === void 0 ? void 0 : _a.call(context, true);
        return function () {
            var _a;
            (_a = context === null || context === void 0 ? void 0 : context.setIsDisabledExport) === null || _a === void 0 ? void 0 : _a.call(context, false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var _d = useForm(), handleSubmit = _d.handleSubmit, reset = _d.reset, setValue = _d.setValue, watch = _d.watch, _e = _d.formState, isDirty = _e.isDirty, isSubmitting = _e.isSubmitting, dirtyFields = _e.dirtyFields;
    var _f = usePendingActions(), showConfirmDialog = _f.showConfirmDialog, pendingAction = _f.pendingAction, executeAction = _f.executeAction, executeActionWithPending = _f.executeActionWithPending, resetPendingAction = _f.resetPendingAction, onCancel = _f.onCancel, onSkipSave = _f.onSkipSave;
    var _g = useMetricSettingsAPI(), metricSettings = _g[0], fetchMetricSettings = _g[1], clearMetricSettings = _g[2];
    var _h = useUpdateMetricSettingsAPI(basePlanId), updatedMetricSettings = _h[0], updateMetricSettings = _h[1], clearUpdateMetricSettings = _h[2];
    // Check Forbidden
    useForbiddenRedirect([metricSettings.statusCode, updatedMetricSettings.statusCode]);
    var metricSettingsDataSorted = useMemo(function () {
        var _a, _b;
        var metrics = (_b = (_a = metricSettings.data) === null || _a === void 0 ? void 0 : _a.metrics) !== null && _b !== void 0 ? _b : [];
        var sortKeys = METRIC_LIST[selectedSection];
        return sortBy(metrics, function (item) {
            var idx = sortKeys.indexOf(item.metric);
            return idx === -1 ? Number.POSITIVE_INFINITY : idx;
        });
    }, [metricSettings.data, selectedSection]);
    var _j = useReportContext(), setIsDirty = _j.setIsDirty, setConfirmExit = _j.setConfirmExit;
    useEffect(function () {
        setIsDirty(isDirty);
    }, [isDirty, setIsDirty]);
    useEffect(function () {
        var confirmExit = function () { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve) {
                        executeActionWithPending(function () { return resolve(true); });
                    })];
            });
        }); };
        setConfirmExit(function () { return confirmExit; });
        return function () { return setConfirmExit(null); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        fetchReportData();
    }, [fetchReportData]);
    useEffect(function () {
        fetchMetricSettings({}, { plan_id: basePlanId, section_type: selectedSection });
        return function () {
            clearMetricSettings();
            clearUpdateMetricSettings();
            reset();
        };
    }, [basePlanId, selectedSection, fetchMetricSettings, clearMetricSettings, clearUpdateMetricSettings, reset]);
    useEffect(function () {
        if (metricSettings.data) {
            setValue('useAbandonRate', metricSettings.data.useAbandonRate);
        }
    }, [metricSettings.data, setValue]);
    // Handle after update metric settings
    useEffect(function () {
        if (updatedMetricSettings.data) {
            if (pendingAction) {
                pendingAction.action();
                resetPendingAction();
            }
            else {
                navigate(generatePath(FULL_REPORT_ROUTER_URL.Actual, { basePlanId: basePlanId }));
            }
        }
        else if (updatedMetricSettings.error) {
            resetPendingAction();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedMetricSettings]);
    var handleSectionChange = function (value) {
        if (isDirty) {
            executeActionWithPending(function () { return setSelectedSection(value); });
            return;
        }
        executeAction(function () { return setSelectedSection(value); });
    };
    var redirectToActual = function () {
        navigate(generatePath(FULL_REPORT_ROUTER_URL.Actual, { basePlanId: basePlanId }));
    };
    var handleCancel = function () {
        if (isDirty) {
            executeActionWithPending(redirectToActual);
            return;
        }
        executeAction(redirectToActual);
    };
    var onSubmit = function (data) { return __awaiter(void 0, void 0, void 0, function () {
        var settingsToSave;
        var _a;
        return __generator(this, function (_b) {
            if (!isDirty) {
                navigate(generatePath(FULL_REPORT_ROUTER_URL.Actual, { basePlanId: basePlanId }));
                return [2 /*return*/];
            }
            settingsToSave = {};
            if (dirtyFields.useAbandonRate) {
                settingsToSave.useAbandonRate = data.useAbandonRate;
            }
            if (dirtyFields.metrics) {
                settingsToSave.metrics = (_a = data.metrics) === null || _a === void 0 ? void 0 : _a.filter(function (item) { return !!item; });
            }
            updateMetricSettings(settingsToSave);
            return [2 /*return*/];
        });
    }); };
    return (_jsxs(FallbackComponent, __assign({ overlay: true, requesting: (metricSettings === null || metricSettings === void 0 ? void 0 : metricSettings.requesting) || (reportData === null || reportData === void 0 ? void 0 : reportData.requesting) }, { children: [_jsx(ErrorMessageBox, { errors: [metricSettings.error, updatedMetricSettings.error] }), _jsxs(DataPanelLayout, __assign({ component: "form", onSubmit: handleSubmit(onSubmit) }, { children: [_jsx(Typography, __assign({ component: "h4", fontWeight: 'bold', fontSize: function (theme) { return theme.typography.pxToRem(28); } }, { children: t('actual.manageSettings.title') })), _jsx(Typography, __assign({ variant: "body1", color: "primary" }, { children: _jsx(Trans, { i18nKey: "actual.manageSettings.subTitle", ns: "report", components: {
                                1: _jsx(Typography, { component: "span", fontWeight: 700, style: { color: METRIC_COLORS.ACCEPTABLE } }),
                                2: _jsx(Typography, { component: "span", fontWeight: 700, style: { color: METRIC_COLORS.CAUTION } }),
                                3: _jsx(Typography, { component: "span", fontWeight: 700, style: { color: METRIC_COLORS.OUTSIDE } }),
                            } }) })), _jsx(ControlPanelSetting, { value: selectedSection, options: SECTION_DATA, onChange: handleSectionChange }), _jsx(Grid, __assign({ container: true }, { children: metricSettingsDataSorted === null || metricSettingsDataSorted === void 0 ? void 0 : metricSettingsDataSorted.map(function (setting, index) {
                            return (_jsx(StyledGridItem, __assign({ item: true, sm: 12 }, { children: _jsx(MetricSlider, __assign({ disabled: disabled, ranges: setting.metadata, metricType: setting.metric, onChange: function (newMetadata) {
                                        setValue("metrics.".concat(index), {
                                            metadata: newMetadata,
                                            id: setting.id,
                                        }, { shouldDirty: true });
                                    } }, { children: setting.metric === METRICS_ENUM.ABANDONMENT_RATE && (_jsx(Box, __assign({ sx: { display: 'flex', justifyContent: 'flex-end' } }, { children: _jsx(FormControlLabel, { control: _jsx(Switch, { disabled: disabled, checked: !!watch('useAbandonRate'), onChange: function (event) {
                                                    setValue('useAbandonRate', event.target.checked, { shouldDirty: true });
                                                } }), sx: { mr: -3 }, label: t('actual.manageSettings.shortAbandons'), labelPlacement: "start" }) }))) })) }), setting.id));
                        }) })), _jsxs(StyledActionBox, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: handleCancel, disabled: isSubmitting }, { children: t('translation:action.cancel') })), disabled ? (_jsx(Button, __assign({ variant: "contained", type: "submit", disabled: disabled }, { children: t('translation:action.done') }))) : (_jsx(LoadingButton, __assign({ variant: "contained", type: "submit", loading: updatedMetricSettings.requesting || isSubmitting }, { children: t('translation:action.done') })))] })] })), _jsx(ConfirmDialog, { open: showConfirmDialog, onClose: onCancel, title: t('translation:confirmation.unsavedChange'), message: t('translation:confirmation.confirmExit'), confirmButtonText: "saveContinue", skipSaveButtonText: "skipSave", onConfirm: handleSubmit(onSubmit), onSkipSaveConfirm: onSkipSave, loading: updatedMetricSettings.requesting })] })));
};
